import { createContext, useContext, type ReactNode } from "react";
import { PlatformLayout } from "@/pages/platform/platform-layout";

// ── AI Agents surface descriptor ─────────────────────────────────────────────
// The AI Agents (Lead Outreach) UI was built platform-first. The same pages are
// now mounted on a tenant workspace too. Rather than fork the components, every
// platform-specific seam (API base, client routes, chrome, and whether the
// surface may spend real money) is captured here and supplied through context.
// The default value is the PLATFORM surface, so the existing /platform/ai-agents
// routes render byte-identically without any provider — only the new tenant
// routes wrap their pages in <AiAgentsSurfaceProvider value={TENANT_…}>.
export interface AiAgentsSurface {
  /** Base path for all AI-Agents API calls (no trailing slash). */
  apiBase: string;
  /** Base client route used for links between the list and per-agent pages. */
  routeBase: string;
  /**
   * Whether this surface may launch/resume runs (i.e. spend real money). Both
   * surfaces are now live: the platform spends platform billing; the tenant
   * surface debits its own Lead Gen credits and routes cold sends through the
   * dedicated relay (with a warm-fallback acknowledgement).
   */
  canLaunch: boolean;
  /**
   * Whether this surface actually enrolls qualified leads into a campaign and
   * sends (tenant) vs. importing prospects only with cold enroll/send deferred
   * (platform). Drives whether the wizard shows the outreach-message step
   * (pick existing campaign | compose fresh) and the cold-domain / warm-fallback
   * acknowledgement. Reply-thread visibility is shown on BOTH surfaces.
   */
  enrollsLeads: boolean;
  /**
   * Whether a warm-mailbox fallback exists when no cold domain is configured.
   * Tenant: true — a tenant can fall back to its connected Gmail with an explicit
   * acknowledgement. Platform: false — platform cold bulk has NO warm fallback;
   * if the cold relay isn't ready the run is hard-blocked, never sent via Gmail.
   */
  allowWarmFallback: boolean;
  /**
   * Whether this surface supports mass-send campaigns. Tenant: true. Platform:
   * false — the platform cold stack is drip-only.
   */
  supportsMass: boolean;
  /**
   * Whether this surface may create/operate "engineer" agents — the weekly
   * self-diagnostics agent (Task #555). Platform-Workspace-ONLY; never exposed
   * on the tenant surface.
   */
  supportsEngineer: boolean;
  /**
   * Chrome wrapper. The platform surface supplies its own PlatformLayout; the
   * tenant surface renders inside the global authenticated app shell (sidebar +
   * top bar), so it uses a passthrough.
   */
  Chrome: React.ComponentType<{ children: ReactNode }>;
}

function Passthrough({ children }: { children: ReactNode }) {
  return <>{children}</>;
}

export const PLATFORM_AI_AGENTS_SURFACE: AiAgentsSurface = {
  apiBase: "/api/platform/ai-agents",
  routeBase: "/platform/ai-agents",
  canLaunch: true,
  enrollsLeads: true,
  allowWarmFallback: false,
  supportsMass: false,
  supportsEngineer: false,
  Chrome: PlatformLayout,
};

// Platform surface, but rendered INSIDE the Super Admin hub (which already
// supplies PlatformLayout). Same API/route/billing as the platform surface,
// only the chrome is a passthrough so the layout isn't double-nested.
export const PLATFORM_AI_AGENTS_HUB_SURFACE: AiAgentsSurface = {
  ...PLATFORM_AI_AGENTS_SURFACE,
  Chrome: Passthrough,
};

export const TENANT_AI_AGENTS_SURFACE: AiAgentsSurface = {
  apiBase: "/api/ai-agents",
  routeBase: "/ai-agents",
  canLaunch: true,
  enrollsLeads: true,
  allowWarmFallback: true,
  supportsMass: true,
  supportsEngineer: false,
  Chrome: Passthrough,
};

const AiAgentsSurfaceContext = createContext<AiAgentsSurface>(
  PLATFORM_AI_AGENTS_SURFACE,
);

export const AiAgentsSurfaceProvider = AiAgentsSurfaceContext.Provider;

export function useAiAgentsSurface(): AiAgentsSurface {
  return useContext(AiAgentsSurfaceContext);
}
